import { CSSProperties, useState } from 'react';
import {
    useActiveUserContext,
    useAssignedAppTypeContext,
    useAuthServiceContext,
    useDataRequestsApiContext,
    useModalApiContext,
    useMyDataRequestsApiContext,
    useStudiesApiContext,
    useToastApiContext
} from '@vivli/core/infrastructure/context';
import { StudyRequestBehaviorEnum } from '@vivli/features/studies/infrastructure/enum';
import { IStudy } from '@vivli/features/studies/infrastructure/interface';
import { finalize, first } from 'rxjs/operators';
import { useNavigate } from 'react-router';
import { PartnerPlatformModalComponent } from './partner-platform-modal.component';
import { IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { Color, Size, Styles } from '@vivli/shared/theme';
import { TranslationComponent } from '@vivli/shared/components';
import { DataRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { AdvancedDropdownMenuComponent } from './advanced-dropdown-menu.component';
import { IDropdownMenuItem } from '@vivli/shared/infrastructure/interface';
import {
    AdminApprovalConstant,
    AssetsConstant,
    DTIAMRCommonConst,
    DTIAMRDropDown,
    DTICommonConst
} from '@vivli/shared/infrastructure/constants';
import { TranslationKeyEnum } from '@vivli/shared/infrastructure/enum';
import { SearchResultRowInfoComponent } from './search-result-row-info.component';
import { ISearchStudy } from '@vivli/features/dashboard/infrastructure/interface';
import { useTranslation } from 'react-i18next';
import { ButtonView } from '@vivli/shared/views';
import { useNavigation } from 'react-router';

const TITLE_FONT_STYLE: React.CSSProperties = {
    fontWeight: Size.FontWeight.Bold,
    fontSize: 20
};
const SEARCH_DETAIL_STYLE: React.CSSProperties = {
    marginTop: Size.TEXTFIELD_ROWS,
    fontSize: Size.FontSize.Large,
    color: Color.TEXT_GRAY
};
const SEARCH_DETAIL_STYLE_TOP: React.CSSProperties = {
    marginTop: Size.TEXTFIELD_ROWS,
    paddingTop: Size.PADDING,
    fontSize: Size.FontSize.Large,
    color: Color.TEXT_GRAY
};
const loginButtonStyle: CSSProperties = {
    ...Styles.Button.BUTTON_VIVLI_BLUE,
    backgroundColor: Color.VIVLI_LIGHT_BLUE,
    fontWeight: Size.FontWeight.Regular,
    marginBottom: Size.PADDING
};
const requestDetailsButtonStyle: CSSProperties = {
    ...Styles.Button.BUTTON_VIVLI_BLUE,
    backgroundColor: Color.LIGHT_GRAY,
    fontWeight: Size.FontWeight.Regular,
    color: Color.BLACK
};
const flexColumnItemsCenterStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
};

const linkStyle: CSSProperties = {
    color: 'white',
    fontWeight: Size.FontWeight.Bold,
    fontSize: Size.FontSize.Large,
    textDecoration: 'underline',
    cursor: 'pointer',
    padding: Size.PADDING
};

interface SearchResultRowComponentProps {
    searchResult?: ISearchStudy;
    innerStyle?: CSSProperties;
    style?: CSSProperties;
    onViewStudyClick?: (e, searchResult: ISearchStudy) => void;
    dataId?: any;
}

export const SearchResultRowComponent = ({ searchResult, innerStyle, style, onViewStudyClick, dataId }: SearchResultRowComponentProps) => {
    const { user: activeUser } = useActiveUserContext();
    const authService = useAuthServiceContext();
    const modalService = useModalApiContext();
    const toastService = useToastApiContext();
    const studiesService = useStudiesApiContext();
    const dataRequestsService = useDataRequestsApiContext();
    const { addDataRequest, myDataRequests } = useMyDataRequestsApiContext();
    const navigate = useNavigate();
    const [isRequestStudyMenuOpen, setIsRequestStudyMenuOpen] = useState(false);
    const [isSubmittingToMyRequest, setIsSubmittingToMyRequest] = useState(false);
    const [addingNewRequest, setIsAddingNewRequest] = useState(false);
    const { isAmr } = useAssignedAppTypeContext();
    const modalMaxWidth = '40em';
    const { t } = useTranslation();
    const studyName = t(TranslationKeyEnum.study);
    const studyNameInitialCap = studyName.charAt(0).toUpperCase() + studyName.slice(1);

    const toastLinkGeneric = (
        <div style={linkStyle} onClick={() => navigate('/admin/requests/draft')}>
            Go to Data Requests
        </div>
    );
    const toastSuccessResponse = (
        <>
            {studyNameInitialCap} added to your data request. {toastLinkGeneric}
        </>
    );
    const toastSuccessResponseNewRequest = (
        <>
            Draft data request created and {studyName} added. {toastLinkGeneric}
        </>
    );

    const containerStyle: CSSProperties = {
        height: '100%',
        minHeight: 190,
        display: 'flex',
        flexDirection: 'row-reverse',
        justifyContent: 'flex-start',
        paddingBottom: Size.PADDING,
        paddingLeft: Size.PADDING,
        paddingRight: Size.PADDING,
        paddingTop: Size.PADDING,
        backgroundColor: Color.WHITE,
        ...Styles.borderRadius(Size.SEARCH_BORDER_RADIUS_SIZE),
        ...innerStyle
    };

    const modalText = (
        <div>
            Enter a descriptive name for your research project.
            <p>
                If this is an additional <TranslationComponent>study</TranslationComponent> you want to add to the same project, then
                instead of entering a new project name here, click cancel and choose your previous project name from the drop-down on the
                "Request <TranslationComponent style={{ textTransform: 'capitalize' }}>study</TranslationComponent>" button.
            </p>
        </div>
    );

    const handleSelectMenuOutsideClick = () => {
        setIsRequestStudyMenuOpen(false);
    };

    const handleRequestButtonClick = () => {
        setIsRequestStudyMenuOpen(!isRequestStudyMenuOpen);
    };

    const handleStudyAdded = (dataRequest: IDataRequest, isNewRequest?: boolean) => {
        addDataRequest(dataRequest);
        const toastToAdd = isNewRequest ? toastSuccessResponseNewRequest : toastSuccessResponse;
        toastService.success(toastToAdd, { autoClose: 10000, closeOnClick: false });
    };

    const handleVivliStudy = (study: IStudy, title: string, isNewRequest?: boolean) => {
        dataRequestsService
            .addStudyToDataRequest({
                targetDataRequestTitle: title,
                studyId: study.id
            })
            .pipe(
                first(),
                finalize(() => setIsSubmittingToMyRequest(false))
            )
            .subscribe((dr) => handleStudyAdded(dr, isNewRequest), modalService.error);
    };

    const handleExternalStudy = ({ externalStudyUri, id }: IStudy, title: string, isNewRequest?: boolean) => {
        const modalTitle = `Add ${t(TranslationKeyEnum.study)} available on a partner platform?`;
        const modalText = `The data package for this ${t(TranslationKeyEnum.study)} is provided on a partner platform. After the ${t(
            TranslationKeyEnum.study
        )} information is added to the data request you will be prompted to request data from that partner platform.`;

        modalService.confirm(modalText, {
            title: modalTitle,
            style: { maxWidth: modalMaxWidth, textAlign: 'left' },
            messageStyle: { textAlign: 'left' },
            onConfirm: () => {
                dataRequestsService
                    .addStudyToDataRequest({
                        targetDataRequestTitle: title,
                        studyId: id
                    })
                    .pipe(
                        first(),
                        finalize(() => setIsSubmittingToMyRequest(false))
                    )
                    .subscribe((dr) => handleStudyAdded(dr, isNewRequest), modalService.error);

                modalService.custom(<PartnerPlatformModalComponent externalUri={externalStudyUri} />, {
                    showCloseButton: true,
                    closeOnOverlayClick: true
                });
            },
            onCancel: () => setIsSubmittingToMyRequest(false)
        });
    };

    const handleStudy = (study, title, isNewRequest?: boolean) => {
        if (study.studyRequestBehavior === StudyRequestBehaviorEnum.Vivli) {
            handleVivliStudy(study, title, isNewRequest);
        } else {
            handleExternalStudy(study, title, isNewRequest);
        }
    };

    const handleAddStudy = (study: IStudy, receivedTitle: string, addingToNewRequest = false) => {
        if (!addingToNewRequest) {
            handleStudy(study, receivedTitle, false);
            return;
        }

        modalService.prompt(modalText, '', {
            title: receivedTitle,
            promptLabel: 'Research Project Name',
            style: { maxWidth: modalMaxWidth, textAlign: 'left' },
            messageStyle: { textAlign: 'left' },
            confirmText: 'OK',
            cancelText: 'Cancel',
            onConfirm: ({ comment }) => {
                handleStudy(study, comment, true);
            },
            onCancel: () => setIsSubmittingToMyRequest(false)
        });
    };

    const addToCart = (selectedItem?: IDropdownMenuItem) => {
        const isNewRequest = selectedItem.value === 'addnewrequest';
        const receivedTitle = isNewRequest ? 'New Research Data Request' : selectedItem.title;
        setIsSubmittingToMyRequest(true);
        setIsAddingNewRequest(isNewRequest);

        studiesService
            .getStudyAnonymous(searchResult.id)
            .pipe(first())
            .subscribe(
                (study) => {
                    handleAddStudy(study, receivedTitle, isNewRequest);
                },
                (error) => {
                    modalService.error(error);
                    setIsSubmittingToMyRequest(false);
                }
            );

        setIsRequestStudyMenuOpen(false);
    };

    if (!searchResult) {
        return null;
    }
    const phase = AdminApprovalConstant.phaseLabels.phaseTable.find((a) => a.value === searchResult.phase);

    return (
        <div
            style={{
                paddingBottom: Size.PADDING,
                ...style
            }}
            data-test-id={dataId}
        >
            <div style={containerStyle}>
                <div>
                    <div>
                        {!activeUser && (
                            <ButtonView
                                style={loginButtonStyle}
                                onClick={() => authService.login()}
                                className="searchResult_logInToRequest"
                                dataId={DTIAMRCommonConst.LoginButton}
                            >
                                Log in to Request <TranslationComponent style={{ textTransform: 'capitalize' }}>study</TranslationComponent>
                            </ButtonView>
                        )}
                        {activeUser && (
                            <div>
                                <AdvancedDropdownMenuComponent
                                    items={(myDataRequests || [])
                                        .filter((dr) => dr.userId === activeUser.userId && dr.status === DataRequestStatusEnum.Draft)
                                        .map(
                                            (dataRequest) =>
                                                ({
                                                    title: dataRequest.requestTitle || dataRequest.id,
                                                    value: dataRequest.id,
                                                    dataRequest
                                                }) as IDropdownMenuItem
                                        )
                                        .concat({
                                            title: '+ Add New Request',
                                            value: 'addnewrequest'
                                        } as IDropdownMenuItem)}
                                    onClickOutside={() => handleSelectMenuOutsideClick()}
                                    onItemClick={(item) => addToCart(item)}
                                    dataId={DTIAMRDropDown.DropdownMenu('item')}
                                >
                                    <ButtonView
                                        isLoading={isSubmittingToMyRequest}
                                        style={
                                            {
                                                ...Styles.Button.BUTTON_VIVLI_BLUE,
                                                backgroundColor: isRequestStudyMenuOpen ? Color.VIVLI_DARK_BLUE : Color.VIVLI_LIGHT_BLUE,
                                                backgroundImage: `url(${AssetsConstant.DOWN_CHEVRON_WHITE})`,
                                                paddingRight: Size.PADDING,
                                                fontWeight: Size.FontWeight.SemiBold,
                                                backgroundRepeat: 'no-repeat',
                                                backgroundPosition: 'right 20px center',
                                                backgroundSize: '20px',
                                                ':active': 'inherit'
                                            } as CSSProperties
                                        }
                                        onClick={() => handleRequestButtonClick()}
                                        className="request_study_menu"
                                        dataId={DTICommonConst.RequestStudyMenu}
                                    >
                                        Request{' '}
                                        <TranslationComponent
                                            style={{
                                                textTransform: 'capitalize'
                                            }}
                                        >
                                            study
                                        </TranslationComponent>
                                    </ButtonView>
                                </AdvancedDropdownMenuComponent>
                            </div>
                        )}
                        {activeUser && (
                            <div
                                style={{
                                    marginBottom: Size.PADDING
                                }}
                            />
                        )}
                        <ButtonView
                            style={requestDetailsButtonStyle}
                            onClick={(e) => onViewStudyClick(e, searchResult)}
                            className="searchResult_viewDetails"
                            dataId={DTIAMRCommonConst.ViewDetailsButton}
                        >
                            View <TranslationComponent style={{ textTransform: 'capitalize' }}>study</TranslationComponent> Details
                        </ButtonView>

                        <div style={flexColumnItemsCenterStyle}>
                            <span style={SEARCH_DETAIL_STYLE_TOP}>
                                {isAmr ? 'Number of isolates:' : 'Number enrolled:'}
                                <span style={{ color: Color.BLACK }}>
                                    {isAmr
                                        ? searchResult.numberOfIsolates !== 0
                                            ? searchResult.numberOfIsolates.toString()
                                            : '-'
                                        : searchResult.actualEnrollment !== 0
                                          ? searchResult.actualEnrollment.toString()
                                          : '-'}
                                </span>
                            </span>
                            {!isAmr && (
                                <span
                                    style={{
                                        ...SEARCH_DETAIL_STYLE,
                                        color: Color.BLACK,
                                        marginTop: 8
                                    }}
                                >
                                    {phase && phase.label}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div
                    style={{
                        flex: 'auto',
                        overflow: 'hidden',
                        paddingRight: Size.PADDING
                    }}
                >
                    <div
                        style={{
                            ...TITLE_FONT_STYLE,
                            color: 'rgb(0, 0, 0)',
                            marginBottom: Size.PADDING
                        }}
                    >
                        {isAmr ? searchResult.program : searchResult.title}
                    </div>
                    <SearchResultRowInfoComponent searchResult={searchResult} isAmr={isAmr} />
                </div>
            </div>
        </div>
    );
};
